/**
 * O que faz: consultas do painel "Leads para retomar hoje", com cache curto.
 * Onde usa: CartaoRetomadaDashboard (resumo) e RetomadaListaModal (lista, só com o modal aberto).
 * Conexões: React Query e retomadaService (RPCs get_dashboard_retomada_resumo_v1 e get_dashboard_retomada_lista_v1).
 * Tabelas: nenhuma direta; o banco lê chat_mensagens, leads, vendas_realizadas e lead_returns.
 * Data: 22/10/2026.
 */
import { useQuery } from '@tanstack/react-query';
import { buscarListaRetomada, buscarResumoRetomada } from '@/services/retomadaService';

// Prefixo único: o modal invalida resumo e lista juntos depois que alguém responde leads por ali.
export const CHAVE_RETOMADA = 'retomada-v1';

// 5 minutos: a lista muda devagar ao longo do dia e cada leitura percorre 8 dias de mensagens da clínica.
const CINCO_MINUTOS = 5 * 60_000;

export function useResumoRetomada(clinicaId: string | null | undefined) {
  return useQuery({
    queryKey: [CHAVE_RETOMADA, clinicaId, 'resumo'],
    queryFn: ({ signal }) => buscarResumoRetomada(clinicaId as string, signal),
    enabled: !!clinicaId,
    staleTime: CINCO_MINUTOS,
    gcTime: 2 * CINCO_MINUTOS,
    retry: 1,
    // Ao voltar para a aba, só recarrega se o resumo tiver mais de 5 minutos (staleTime). Corrige o cartão
    // esquecido aberto desde antes da meia-noite: ele mostraria os números do dia anterior como se fossem de hoje.
    refetchOnWindowFocus: true,
  });
}

export function useListaRetomada(clinicaId: string | null | undefined, habilitado: boolean) {
  return useQuery({
    queryKey: [CHAVE_RETOMADA, clinicaId, 'lista'],
    queryFn: ({ signal }) => buscarListaRetomada(clinicaId as string, signal),
    // Nome e telefone só são buscados quando alguém abre o modal.
    enabled: !!clinicaId && habilitado,
    staleTime: CINCO_MINUTOS,
    gcTime: 2 * CINCO_MINUTOS,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}
